import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { LetDirective } from '@ngrx/component';
import { PorscheDesignSystemModule } from '@porsche-design-system/components-angular';
import {
  BehaviorSubject,
  ReplaySubject,
  distinctUntilChanged,
  shareReplay,
  tap,
  type Observable,
} from 'rxjs';
import type { InventoryType } from '../../shared/models/api/inventory-type.model';
import { InventoryTypeService } from '../../shared/services/api/inventory-type.service';
import { MediaQueryService } from '../../shared/services/media-query.service';
import { InventoryTypeCreateComponent } from './inventory-type-create/inventory-type-create.component';
import { InventoryTypeDeleteComponent } from './inventory-type-delete/inventory-type-delete.component';
import { InventoryTypeEditComponent } from './inventory-type-edit/inventory-type-edit.component';

@Component({
  selector: 'opa-inventory-item-type-list',
  imports: [
    PorscheDesignSystemModule,
    CommonModule,
    RouterLink,
    LetDirective,
    InventoryTypeCreateComponent,
    InventoryTypeEditComponent,
    InventoryTypeDeleteComponent,
  ],
  templateUrl: './inventory-item-type-list.component.html',
  styleUrl: './inventory-item-type-list.component.scss',
})
export class InventoryItemTypeListComponent {
  readonly mediaQueryService = inject(MediaQueryService);
  private readonly inventoryTypeService = inject(InventoryTypeService);

  private readonly loading = new BehaviorSubject(false);
  private readonly inventoryType = new ReplaySubject<InventoryType | null>(1);
  readonly mediaMaxXs$ = this.mediaQueryService.max('xs');
  readonly loading$ = this.loading.pipe(distinctUntilChanged());
  readonly inventoryType$ = this.inventoryType.asObservable();

  data$ = this.getData$();
  showCreateModal = false;
  showDeleteModal = false;
  showEditModal = false;

  trackInventoryType(index: number, type: InventoryType): number {
    return type.id;
  }

  reloadData(): void {
    this.data$ = this.getData$();
  }

  private getData$(): Observable<InventoryType[]> {
    this.loading.next(true);

    return this.inventoryTypeService.getInventoryTypes().pipe(
      tap({
        next: () => this.loading.next(false),
        error: () => this.loading.next(false),
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  openDeleteModal(inventoryType: InventoryType): void {
    this.inventoryType.next(inventoryType);
    this.showDeleteModal = true;
  }

  closeDeleteModal(inventoryType?: InventoryType): void {
    this.showDeleteModal = false;
    if (inventoryType) this.reloadData();
    this.inventoryType.next(null);
  }

  openEditModal(inventoryType: InventoryType): void {
    this.inventoryType.next(inventoryType);
    this.showEditModal = true;
  }

  closeEditModal(success?: boolean): void {
    this.showEditModal = false;
    if (success) this.reloadData();
    this.inventoryType.next(null);
  }
}
